import { FC, useCallback, useMemo, useState } from 'react'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { AsH1, AsSpan, EyebrowMD, TitleLG } from '@/atoms/Text'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGiveawayContext } from '../../context/GiveawayContext'
import { formatReleaseDate } from '../../utils/dateUtils'
import { GiveawayVideoModal } from '../Modals/GiveawayVideoModal'
import { GiveawayLogo } from './GiveawayLogo'
import { GiveawayPlayButton } from './GiveawayPlayButton'
import { GiveawayPromoBanner } from './GiveawayPromoBanner'

const GiveawayHero: FC = () => {
  const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false)
  const { t } = useTranslate('tickets')
  const { push } = useSafeRouter()
  const { track } = useSafeAnalytics()
  const { content, theatricalSlug, scrollToProducts, sweepstake, togglePauseBackgroundVideo, isActive, phase } =
    useGiveawayContext()
  const { releaseDate } = content
  const { hero, youtubeTrailerId } = content
  const { projectName, winnerAnnouncedDate } = sweepstake

  const showInTheatersBadge = releaseDate && phase !== 'guild_access'
  const trailerId = useMemo(() => {
    return hero?.giveawayYoutubeId ?? youtubeTrailerId
  }, [hero?.giveawayYoutubeId, youtubeTrailerId])

  const videoButtonText = hero?.giveawayYoutubeId
    ? t('watchGiveawayHeroVideo', 'Watch Giveaway Video')
    : t('watchVideo', 'Watch Video')

  const handleOpenTrailer = useCallback(() => {
    setIsTrailerModalOpen(true)
    togglePauseBackgroundVideo(true)
    track('Giveaway Video Clicked')
  }, [togglePauseBackgroundVideo, track])

  const handleCloseTrailer = useCallback(() => {
    setIsTrailerModalOpen(false)
    togglePauseBackgroundVideo(false)
  }, [togglePauseBackgroundVideo])

  const { heroTitle, btnText, onClick } = useMemo(() => {
    if (!isActive && phase === 'theatrical') {
      return {
        heroTitle: t('projectNowInTheaters', '{{projectName}} now in theaters', { projectName }),
        btnText: t('getShowtimes', 'Get Showtimes'),
        onClick: () => push(`/tickets/${theatricalSlug}`),
      }
    }

    if (!isActive && phase === 'guild_access') {
      return {
        heroTitle: t('watchProjectNow', 'Watch {{projectName}} Now', { projectName }),
        btnText: t('watchNow', 'Watch Now'),
        onClick: () => push(`/movies/${theatricalSlug}`),
      }
    }

    if (isActive && phase === 'pre_sale') {
      return {
        heroTitle: hero?.title,
        btnText: t('preOrderTicketsV2', 'Preorder Tickets'),
        onClick: scrollToProducts,
      }
    }

    return { heroTitle: hero?.title, btnText: null, onClick: scrollToProducts }
  }, [isActive, phase, hero?.title, scrollToProducts, t, projectName, push, theatricalSlug])

  return (
    <div className="flex flex-1 flex-col">
      <div className="relative m-auto flex w-full max-w-[1080px] flex-1 flex-col gap-4 pt-4 md:flex-row md:items-center md:justify-center md:gap-10 md:pt-0">
        <FlexColumn className="gap-4 md:shrink-0">
          <GiveawayLogo />

          {showInTheatersBadge && (
            <InTheatersPill
              text={t('inTheatersReleaseDate', 'In Theaters {{releaseDate}}', {
                releaseDate: formatReleaseDate(releaseDate),
              })}
            />
          )}

          <TitleLG
            className="md:photon-heading-lg text-balance text-center md:max-w-[540px] md:text-left"
            as={AsH1}
            color="white"
            weight="bold"
          >
            {heroTitle}
          </TitleLG>

          <GiveawayPromoBanner
            isActive={isActive}
            theatricalSlug={theatricalSlug}
            winnerAnnouncedDate={winnerAnnouncedDate}
          />

          {btnText && (
            <div>
              <Button
                size="lg"
                className="mr-auto mt-4 hidden border-none !bg-giveaway-secondary !font-medium md:flex"
                onClick={onClick}
                variant="core-gray-900"
              >
                {btnText}
              </Button>
            </div>
          )}
        </FlexColumn>

        {trailerId && (
          <div className="mx-auto h-full flex-1 items-center justify-center md:flex md:w-full">
            <GiveawayPlayButton
              text={videoButtonText ?? t('watchTrailer', 'Watch Trailer')}
              onClick={handleOpenTrailer}
            />
          </div>
        )}

        {btnText && (
          <FlexColumn className="gap-6 md:hidden">
            <Button
              className="border-none !bg-giveaway-secondary !font-medium"
              onClick={onClick}
              variant="core-gray-900"
              size="lg"
            >
              {btnText}
            </Button>
          </FlexColumn>
        )}
      </div>

      <GiveawayVideoModal
        handleClose={handleCloseTrailer}
        showModal={isTrailerModalOpen}
        youTubeUrl={`https://www.youtube-nocookie.com/embed/${trailerId}?autoplay=1`}
      />
    </div>
  )
}

export default GiveawayHero

const InTheatersPill = ({ text }: { text: string }) => {
  return (
    <EyebrowMD
      as={AsSpan}
      weight="semibold"
      className="mx-auto w-fit items-center justify-center bg-giveaway-primary px-2 py-1 text-white md:mx-0 md:mt-4"
    >
      {text}
    </EyebrowMD>
  )
}
